import { Grid, LinearProgress, Typography } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { progressAtom } from "../../../components/wizard/atoms";
import {
  FooterActions,
  TipProps,
} from "../../../components/wizard/footer-actions.component";
import {
  GamePlanPath,
  GamePlanWorkout,
} from "../../../components/wizard/use-game-plan";
import { Status, WorkoutDetails } from "../../../models/gameplan";
import { Profile } from "../../../models/profile";
import { setWorkoutStatusById } from "../../../shared/slices/data";
import { RootState } from "../../../store";
import { WorkoutAnswer } from "../../components/workout-answer/workout-answer.component";
import { saveWorkout } from "../../learningPath.service";
import { workoutStyles } from "./workout.styles";

interface WorkoutProps {
  gamePlan: GamePlanPath;
}

export const Workout = ({ gamePlan }: WorkoutProps) => {
  const dispatch = useDispatch();
  const classes = workoutStyles();
  const workout = gamePlan.item as GamePlanWorkout;

  const workoutDetails = {
    ...workout,
    answers: JSON.parse(workout.answers),
    questionId: workout.id,
  } as WorkoutDetails;

  const [selectedAnswer, setSelectedAnswer] = useState<string>();
  const [isValidated, setIsValidated] = useState<boolean>();

  const { participantProfileId } = useSelector<RootState, Profile>(
    (state) => state.profile
  );
  const setProgress = useSetAtom(progressAtom);

  useEffect(() => {
    setIsValidated(false);
    setSelectedAnswer(workout?.selectedAnswer);
  }, [workout]);

  useEffect(() => {
    setProgress(50);
  }, [setProgress]);

  const {
    mutate,
    mutateAsync,
    isLoading: isUpdating,
  } = useMutation(saveWorkout, {
    onSuccess: (_, variables) => {
      dispatch(setWorkoutStatusById(variables.workoutId, variables.status));
    },
  });

  const originalAnswer = useRef<string>();
  useEffect(() => {
    if (gamePlan.status === Status.NEW && !isUpdating)
      mutate({
        workoutId: workout.id,
        status: Status.IN_PROGRESS,
      });

    originalAnswer.current = (gamePlan.item as GamePlanWorkout).selectedAnswer;
  }, [workout.id, mutate, gamePlan.status, gamePlan.item]);

  const onChecked = (value: string) => {
    setIsValidated(false);
    setSelectedAnswer(value);
  };

  const onSuccessContinue = async () => {
    if (originalAnswer.current != selectedAnswer) {
      await mutateAsync({
        workoutId: workout.id,
        status: Status.COMPLETED,
        answer: selectedAnswer,
      });
    }
  };

  const onVerify = async () => {
    setIsValidated(true);
  };

  const cardinalNumber = (answer) => {
    switch (answer) {
      case "a":
        return "first";
      case "b":
        return "second";
      case "c":
        return "third";
      case "d":
      default:
        return "forth";
    }
  };

  const calculateFeedback = (): TipProps => {
    const isCorrectAnswer = selectedAnswer == workoutDetails.correctAnswer;
    const variant = isCorrectAnswer ? "success" : "error";
    const title = `Oops! The right answer is the ${cardinalNumber(
      workoutDetails.correctAnswer
    )} option (${workoutDetails.correctAnswer})`;
    const details = workoutDetails.answerJustification;

    return { variant, title, details };
  };

  return (
    <>
      {isUpdating && (
        <div data-testid="loading">
          <LinearProgress color="secondary" />
        </div>
      )}
      <Grid item xs={12}>
        <Typography variant="h6" className="title">
          {workoutDetails?.title}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" className={classes.question}>
          {workoutDetails?.question}
        </Typography>
      </Grid>
      <WorkoutAnswer
        participantProfileId={participantProfileId}
        workoutDetails={workoutDetails}
        isDisabled={false}
        onChecked={onChecked}
        answerSelected={selectedAnswer}
        answers={workoutDetails?.answers || []}
      />
      <FooterActions
        backShow={!isValidated}
        nextText={!isValidated ? "Verify" : "Continue"}
        nextAction={!isValidated ? onVerify : onSuccessContinue}
        nextPreventStep={!isValidated}
        nextDisabled={!selectedAnswer || isUpdating}
        tip={isValidated ? calculateFeedback() : undefined}
      />
    </>
  );
};
